/**
 * BackButton — square ghost button for returning to the parent screen.
 *
 * Two modes:
 *   1. Explicit parent (preferred): pass `to` and it navigates via
 *      TanStack Link. Users can middle-click / cmd-click to open in a
 *      new tab — that's the point of an actual link over history.back().
 *   2. Implicit: omit `to` and it calls window.history.back(). Use this
 *      only when the parent route is genuinely ambiguous (e.g. a detail
 *      page that can be reached from two different list views).
 *
 * Visual is a 32×32 square with a chevron, matching the shell's icon
 * buttons. Pairs with <PageHeader back={<BackButton to="..." />}>.
 */

import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

export interface BackButtonProps {
  /** Parent route to return to. Preferred — gives a real <a> for cmd-click. */
  to?: string;
  /** aria-label override. Default "Back". */
  label?: string;
}

export function BackButton({ to, label = 'Back' }: BackButtonProps) {
  const baseClasses =
    'inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-bg-canvas text-text-secondary transition hover:border-border-strong hover:bg-surface-raised hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2';

  if (to) {
    return (
      <Link to={to} aria-label={label} className={baseClasses}>
        <ArrowLeft className="h-4 w-4" />
      </Link>
    );
  }

  return (
    <button
      type="button"
      aria-label={label}
      className={baseClasses}
      onClick={() => window.history.back()}
    >
      <ArrowLeft className="h-4 w-4" />
    </button>
  );
}
